import { AIMessage } from "@/features/Prompt/schema";
import { safeParseSchemaWithSentry } from "@/features/Sentry/utils";
import { ThreadMessage, ThreadRuntime } from "@assistant-ui/react";
import { z } from "zod";
import { MessageConverter } from "./initThread/messageConverter";
import {
  detectClientFromPath,
  getClientConversationsRoute,
} from "./routeUtils";

/**
 * Determines if the current thread is a new thread based on URL and runtime state
 * @param isCurrentlyNew - Current 'isNew' flag
 * @param threadId - Thread ID from URL
 * @param isRunning - Whether the thread is currently running
 * @returns Updated isNew state
 */
export const determineIfNewThread = (
  isCurrentlyNew: boolean,
  threadId: string | undefined,
  isRunning: boolean,
): boolean => {
  if (isCurrentlyNew === true || threadId === undefined) {
    return true;
  }

  if (isRunning) {
    return false;
  }

  return isCurrentlyNew;
};

/**
 * Checks if a thread has just completed its execution
 * @param isNew - Whether this is a new thread
 * @param isRunning - Current running state
 * @param wasRunning - Previous running state
 * @returns True if thread just completed, false otherwise
 */
export const hasThreadJustCompleted = (
  isNew: boolean,
  isRunning: boolean,
  wasRunning: boolean,
): boolean => {
  return isNew && isRunning === false && wasRunning === true;
};

/**
 * Handles thread completion by refreshing conversations and navigating
 * @param threadId - Thread ID from URL
 * @param queryClient - React Query client
 * @param navigate - React Router navigate function
 */
export const handleThreadCompletion = (
  threadId: string | undefined,
  queryClient: any,
  navigate: (path: string) => void,
): void => {
  if (threadId) {
    queryClient.invalidateQueries({ queryKey: ["threads"] });

    // Detect client from current path and navigate accordingly
    const client = detectClientFromPath(window.location.pathname);
    navigate(getClientConversationsRoute(client, threadId));
  }
};

/**
 * Verifies if a conversation is already loaded in the cache
 * @param loadedConversations - Map of loaded conversations
 * @param threadId - ID of the thread to check
 * @returns True if conversation is already loaded
 */
export const isConversationAlreadyLoaded = (
  loadedConversations: Map<string, string>,
  threadId: string,
): boolean => {
  return loadedConversations.has(threadId);
};

/**
 * Switches the runtime to an existing thread
 * @param assistantRuntime - The assistant runtime
 * @param loadedConversations - Map of loaded conversations
 * @param threadId - ID of the thread to switch to
 */
export const switchToExistingThread = (
  assistantRuntime: any,
  loadedConversations: Map<string, string>,
  threadId: string,
): void => {
  const threadInternalId = loadedConversations.get(threadId);
  if (threadInternalId) {
    assistantRuntime.threads.switchToThread(threadInternalId);
  }
};

/**
 * Creates a new thread in the assistant runtime
 * @param assistantRuntime - The assistant runtime
 * @returns Promise that resolves when thread is created
 */
export const createNewThread = async (assistantRuntime: any): Promise<void> => {
  return assistantRuntime.threads.switchToNewThread();
};

/**
 * Imports conversation data into the thread runtime
 * @param conversationData - Raw conversation data from API
 * @param schema - Zod schema for validation
 * @param threadRuntime - Thread runtime to import messages into
 * @returns True if import was successful
 */
export const processAndImportConversation = (
  conversationData: ReadonlyArray<AIMessage>,
  schema: z.ZodTypeAny,
  threadRuntime: ThreadRuntime,
): boolean => {
  const parsedData = safeParseSchemaWithSentry(schema, conversationData);

  if (parsedData.success) {
    const messages = parsedData.data as ReadonlyArray<AIMessage>;
    const repository =
      MessageConverter.convertToThreadExportMessageRepository(messages);
    threadRuntime.import(repository);
    return true;
  }
  console.error("Error while parsing conversation data.");
  return false;
};

/**
 * Stores a reference to a loaded conversation
 * @param threadListItemRuntime - Runtime for thread list items
 * @param loadedConversations - Map to store loaded conversations
 * @param threadId - External thread ID (from API)
 * @returns Promise that resolves to the internal thread ID
 */
export const storeThreadReference = async (
  threadListItemRuntime: any,
  loadedConversations: Map<string, string>,
  threadId: string,
): Promise<string> => {
  const threadListItem = await threadListItemRuntime.initialize();
  const threadInternalId = threadListItem.remoteId;

  loadedConversations.set(threadId, threadInternalId);
  return threadInternalId;
};

/**
 * Finds the parent message ID from previous messages
 * @param messages - Array of thread messages
 * @returns The parent ID or null if not found
 */
export function findParentId(
  messages: readonly ThreadMessage[],
): string | null {
  for (let i = messages.length - 2; i >= 0; i--) {
    if (messages[i]?.metadata?.custom?.backend_id) {
      return messages[i].metadata.custom.backend_id as string;
    }
  }

  return null;
}

/**
 * Extracts the last user message from a thread
 * @param messages - Array of thread messages
 * @returns The content of the last user message
 */
export const getLastUserMessage = (
  messages: readonly ThreadMessage[],
): string => {
  if (!messages.length) return "";

  const lastUserMessage = messages[messages.length - 1];

  if (!lastUserMessage?.content) return "";

  if (typeof lastUserMessage.content === "string") {
    return lastUserMessage.content;
  }

  const textParts = lastUserMessage.content.filter(
    (part) => part.type === "text",
  );
  if (textParts.length > 0) {
    const texts = textParts.map((part) => (part as any).text || "");
    return texts.join(" ");
  }

  return "";
};
